<template>
    <CommonDialog :value="value"
                  :fullscreen="false"
                  width="820"
                  :title="edit == true ? $t('Edit') : $t('Add')"
                  show-action-toolbar
                  @input="$emit('input', false)">
                      <v-form ref="structureForm" v-model="valid" lazy-validation class="structure-form">
                          <!-- status -->
            <div class="structure-form__status" :class="{ 'is-off': !structure.active }">
                <span class="structure-form__status-icon">
                    <v-icon>{{ structure.active ? 'mdi-check-circle-outline' : 'mdi-pause-circle-outline' }}</v-icon>
                </span>
                <div class="structure-form__status-text">
                    <div class="structure-form__status-title">{{ $t('Active') }}</div>
                    <div class="structure-form__status-hint">
                        {{ tr('ActiveStructureHint', 'Inactive structures stay in the system but are hidden from selection lists.', 'الهياكل غير المفعّلة تبقى في النظام ولكن لا تظهر في قوائم الاختيار.') }}
                    </div>
                </div>
                <v-switch v-model="structure.active"
                          class="structure-form__switch"
                          color="primary"
                          inset
                          dense
                          hide-details
                          :aria-label="$t('Active')" />
            </div>

            <!-- general information -->
            <section class="structure-form__section">
                <h3 class="structure-form__legend">
                    <v-icon small class="me-2">mdi-card-text-outline</v-icon>
                    {{ tr('GeneralInformation', 'General information', 'البيانات الأساسية') }}
                </h3>

                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-text-field v-model="structure.nameAr"
                                      dense
                                      outlined
                                      prepend-inner-icon="mdi-form-textbox"
                                      :label="$t('NameAr')"
                                      :rules="nameRules"
                                      required />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field v-model="structure.nameEn"
                                      dense
                                      outlined
                                      prepend-inner-icon="mdi-form-textbox"
                                      :label="$t('NameEn')"
                                      :rules="nameRules"
                                      required />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field v-model="structure.code"
                                      dense
                                      outlined
                                      prepend-inner-icon="mdi-identifier"
                                      :label="$t('Code')"
                                      :rules="nameRules"
                                      required />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-autocomplete v-model="structure.typeId"
                                        dense
                                        outlined
                                        prepend-inner-icon="mdi-shape-outline"
                                        :loading="loading"
                                        :rules="nameRules"
                                        item-text="name"
                                        item-value="id"
                                        :items="structureTypes"
                                        :label="$t('Type')" />
                    </v-col>
                    <v-col cols="12">
                        <v-textarea v-model="structure.description"
                                    outlined
                                    rows="3"
                                    hide-details
                                    auto-grow
                                    :label="$t('Description')" />
                    </v-col>
                </v-row>
            </section>

            <!-- hierarchy -->
            <section class="structure-form__section">
                <h3 class="structure-form__legend">
                    <v-icon small class="me-2">mdi-file-tree-outline</v-icon>
                    {{ tr('Hierarchy', 'Hierarchy', 'التسلسل الهرمي') }}
                </h3>

                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-autocomplete v-model="structure.parentId"
                                        dense
                                        outlined
                                        clearable
                                        prepend-inner-icon="mdi-sitemap-outline"
                                        :loading="loading"
                                        item-text="name"
                                        item-value="id"
                                        :items="structures"
                                        :label="$t('ParentStructure')"
                                        :hint="tr('ParentStructureHint', 'Leave empty to create a top level structure.', 'اتركه فارغاً لإنشاء هيكل في المستوى الأعلى.')"
                                        persistent-hint />
                    </v-col>
                </v-row>
            </section>
        </v-form>

        <template v-slot:actions>
            <v-btn v-if="!edit"
                   depressed
                   small
                   color="secondary"
                   class="structure-form__btn white--text"
                   :disabled="posting"
                   @click="save()">
                <v-icon small class="me-1">mdi-check</v-icon>
                {{ $t("Add") }}
            </v-btn>
            <v-btn v-if="edit"
                   depressed
                   small
                   color="secondary"
                   class="structure-form__btn white--text"
                   :disabled="posting"
                   @click="update()">
                <v-icon small class="me-1">mdi-content-save-outline</v-icon>
                {{ $t("Update") }}
            </v-btn>
            <v-btn depressed
                   small
                   class="structure-form__btn structure-form__btn--muted ms-3"
                   :disabled="posting"
                   @click="$emit('input', false); resetStructureObj()">
                {{ $t("Cancel") }}
            </v-btn>
        </template>
    </CommonDialog>
</template>

<script>
    import CommonDialog from '@/components/controls/CommonDialog'
    import LookupsService from '@/services/LookupsService.js'
    import StructuresService from '@/services/StructuresService.js'

    export default {
        components: {
            CommonDialog
        },

        props: {
            value: {
                type: Boolean,
                default: false
            },
            edit: {
                type: Boolean,
                default: false
            },
            structureId: {
                type: String,
                default: null
            },
            title: {
                type: String,
                default: ''
            }
        },

        data() {
            return {
                valid: true,
                loading: false,
                posting: false,
                structureTypes: [],
                structures: [],
                nameRules: [
                    v => !!v || this.$t('RequiredField')
                ],
                structure: {
                    id: null,
                    nameAr: '',
                    nameEn: '',
                    code: '',
                    description: '',
                    typeId: null,
                    parentId: null,
                    active: true,
                }
            }
        },

        created() {
            this.listLookups()

            if (this.edit) {
                this.loadStructure()
            }
        },

        methods: {

            // translate with a built-in fallback, so labels still read correctly when
            // the key has not been added to the translations table yet
            tr(key, fallbackEn, fallbackAr) {
                if (this.$te(key)) {
                    return this.$t(key)
                }
                return this.$i18n.locale === 'ar' ? fallbackAr : fallbackEn
            },

            loadStructure() {
                StructuresService.getStructure(this.structureId).then((res) => {
                    if (res.data) {
                        this.structure = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            listLookups() {
                this.loading = true
                let promises = [
                    LookupsService.listStructureTypes(),
                    LookupsService.listStructures()
                ]

                Promise.all(promises)
                    .then(
                        ([
                            structureTypes,
                            structures
                        ]) => {
                            this.structureTypes = structureTypes.data
                            this.structures = structures.data
                        },
                    ).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
            },

            save() {
                if (this.$refs.structureForm.validate()) {
                    StructuresService.addStructure(this.structure).then((res) => {
                        this.$refs.structureForm.resetValidation()
                        this.resetStructureObj()
                        this.$emit('update', true)

                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },


            update() {
                if (this.$refs.structureForm.validate()) {
                    StructuresService.updateStructure(this.structureId, this.structure).then((res) => {
                        this.$refs.structureForm.resetValidation()
                        this.resetStructureObj()
                        this.$emit('update', true)
                        this.$emit('input', false)
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },

            resetStructureObj() {
                this.structure.id = null
                this.structure.nameAr = ''
                this.structure.nameEn = ''
                this.structure.code = ''
                this.structure.description = ''
                this.structure.typeId = null
                this.structure.parentId = null
                this.structure.active = true
            }
        },
    }
</script>

<style>
    /* Structure add/edit dialog — the dialog is teleported outside the page,
       so these rules are namespaced by the .structure-form class instead. */
    .structure-form {
        padding: 18px 20px 4px;
    }

    .structure-form__status {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        margin-bottom: 22px;
        border-radius: 12px;
        border: 1px solid #d8e6e4;
        background: #f2f8f7;
        transition: background .2s ease, border-color .2s ease;
    }

    .structure-form__status.is-off {
        border-color: #e4e9ee;
        background: #f5f7f9;
    }

    .structure-form__status-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        flex: 0 0 40px;
        border-radius: 11px;
        background: #fff;
        border: 1px solid #dfe7ea;
    }

    .structure-form__status-icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .structure-form__status.is-off .structure-form__status-icon .v-icon {
        color: #8b9aa6 !important;
    }

    .structure-form__status-text {
        min-width: 0;
        flex: 1 1 auto;
    }

    .structure-form__status-title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: #2b3a42;
        line-height: 1.4;
    }

    .structure-form__status-hint {
        font-size: 12.5px;
        color: #78899a;
        line-height: 1.5;
    }

    .structure-form__switch {
        margin-top: 0 !important;
        padding-top: 0 !important;
        flex: 0 0 auto;
    }

    .structure-form__section {
        margin-bottom: 20px;
    }

    .structure-form__legend {
        display: flex;
        align-items: center;
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: .03em;
        text-transform: uppercase;
        color: #78899a;
        margin: 0 0 12px;
    }

    .structure-form__legend .v-icon {
        color: #78899a !important;
    }

    .structure-form .v-input--is-focused .v-input__slot fieldset {
        border-width: 2px;
    }

    .structure-form__btn {
        border-radius: 10px;
        text-transform: none;
        letter-spacing: 0;
        font-weight: 600;
    }

    .structure-form__btn--muted {
        background: #eef1f4 !important;
        color: #2b3a42 !important;
    }

    @media (max-width: 599px) {
        .structure-form {
            padding: 14px 12px 4px;
        }

        .structure-form__status {
            padding: 12px;
            gap: 10px;
        }

        .structure-form__status-hint {
            display: none;
        }
    }
</style>
